import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  useAppContext,
  useAudioCreditsEstimation,
  useCreditContext,
  useSaveAudio,
} from '../../hooks';
import { AudioCredit } from '../../@types';

interface Props {
  text: string;
  audioId: string;
  name: string;
  shouldFetchCreditDetails: boolean;
  handleOnSuccess: () => void;
  handleOnCloseDialog: () => void;
}

export const useAudioCredit = ({
  text,
  audioId,
  name,
  shouldFetchCreditDetails,
  handleOnSuccess,
  handleOnCloseDialog,
}: Props) => {
  const [audioCreditDetails, setAudioCreditDetails] = useState<AudioCredit | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: mutateSaveAudio, isLoading: isSavingAudio } = useSaveAudio();
  const { mutate: mutateGetAudioCreditsEstimation, isLoading: isFetchingAudioCredits } =
    useAudioCreditsEstimation();
  const [errorFetchingCredits, setErrorFetchingCredits] = useState(false);
  const { handleUpdateUserCredit } = useCreditContext();
  const [textToSpeechId, setTextToSpeechId] = useState('');
  const {
    websocket: { sendMessage },
  } = useAppContext();

  useEffect(() => {
    if (shouldFetchCreditDetails) {
      mutateGetAudioCreditsEstimation(
        { text, isAudioCreation: true },
        {
          onSuccess: (data) => {
            setAudioCreditDetails(data);
          },
          onError: (error: any) => {
            console.error('Failed to fetch audio credit details:', error);
          },
        }
      );
    }
  }, [text, shouldFetchCreditDetails]);

  const handleOnSave = () => {
    mutateSaveAudio(
      { text, audioId, name },
      {
        onSuccess: (data) => {
          enqueueSnackbar(
            data?.isAlreadyExists ? 'Audio already exists' : 'Audio creation in progress',
            {
              variant: data?.isAlreadyExists ? 'info' : 'success',
            }
          );
          setTextToSpeechId(data.id);
          handleOnSuccess();
          handleUpdateUserCredit();
          sendMessage({ event: 'register', data: { id: data.id } });
        },
        onError: (error: any) => {
          enqueueSnackbar(error?.message || 'Failed to save audio', { variant: 'error' });
          setErrorFetchingCredits(true);
          handleOnCloseDialog();
        },
      }
    );
  };

  const availableCredit = Math.max(
    (audioCreditDetails?.availableCredits || 0) - (audioCreditDetails?.creditsInUse || 0),
    0
  );

  const isEnoughCredit = availableCredit >= (audioCreditDetails?.requiredCredits || 0);

  return {
    textToSpeechId,
    availableCredit,
    requiredCredit: audioCreditDetails?.requiredCredits,
    isFetchingAudioCredits,
    errorFetchingCredits,
    isEnoughCredit,
    isSavingAudio,
    handleOnSave,
    audioCreditDetails,
  };
};
